import { useEffect, useState, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mic, QrCode } from 'lucide-react'
import { apiClient } from '../services/api'
import { ui } from '../styles/ui'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Card from '../components/ui/Card'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import StatusBadge from '../components/StatusBadge'
import { formatCurrency } from '../utils/currency'
import { formatDateTime } from '../utils/date'
import { formatCategoryName, formatConfidence, formatEmptyValue } from '../utils/displayValue'
import { useTranslation } from '../contexts/TranslationContext'

const REPROCESS_STRATEGY_OPTIONS = {
  qrcode: [
    { value: 'cache', labelKey: 'Cache' },
    { value: 'selector', labelKey: 'Selector' },
    { value: 'llm', labelKey: 'LLM' },
  ],
  image: [
    { value: 'cache', labelKey: 'Cache' },
    { value: 'fresh', labelKey: 'Fresh OCR (bypass cache)' },
  ],
  long_receipt_auto_scan: [
    { value: 'fresh', labelKey: 'Fresh OCR (bypass cache)' },
  ],
  speech: [
    { value: 'default', labelKey: 'Default' },
  ],
}

const DEFAULT_REPROCESS_STRATEGY = {
  // WHY: QR reprocess should prefer the persisted full parse cache so it does
  // not downgrade receipts to selector-only data unless the user explicitly asks.
  qrcode: 'cache',
  image: 'fresh',
  long_receipt_auto_scan: 'fresh',
  speech: 'default',
}

function normalizeSourceType(sourceType) {
  const normalized = String(sourceType || '').trim().toLowerCase()
  return ['qrcode', 'image', 'speech', 'long_receipt_auto_scan'].includes(normalized) ? normalized : 'image'
}

function sourceTypeLabel(t, sourceType) {
  switch (String(sourceType || '').trim().toLowerCase()) {
    case 'long_receipt_auto_scan':
      return t('receipts.source.long_receipt_auto_scan', 'Long receipt (auto capture)')
    case 'qrcode':
      return 'qrcode'
    case 'speech':
      return 'speech'
    case 'image':
      return 'image'
    default:
      return t('Unknown', 'Unknown')
  }
}

function resolveReprocessStrategyLabel(t, labelKey) {
  switch (labelKey) {
    case 'Cache':
      return t('receipts.reprocess_strategy.cache', 'Cache')
    case 'Selector':
      return t('receipts.reprocess_strategy.selector', 'Selector')
    case 'LLM':
      return t('receipts.reprocess_strategy.llm', 'LLM')
    case 'Fresh OCR (bypass cache)':
      return t('receipts.reprocess_strategy.fresh_ocr', 'Fresh OCR (bypass cache)')
    case 'Default':
      return t('receipts.reprocess_strategy.default', 'Default')
    default:
      return labelKey
  }
}

export default function Receipts() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [receipts, setReceipts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedReceiptIds, setSelectedReceiptIds] = useState([])
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingReceiptIds, setPendingReceiptIds] = useState([])
  const [reprocessingIds, setReprocessingIds] = useState([])
  const [reprocessConfirmOpen, setReprocessConfirmOpen] = useState(false)
  const [reprocessPendingIds, setReprocessPendingIds] = useState([])
  const [reprocessMessage, setReprocessMessage] = useState('')
  const [reviewConfirmOpen, setReviewConfirmOpen] = useState(false)
  const [reviewPendingIds, setReviewPendingIds] = useState([])
  const [reviewMessage, setReviewMessage] = useState('')
  const [reviewAction, setReviewAction] = useState('review') // 'review' or 'unreview'
  const [reprocessStrategy, setReprocessStrategy] = useState('cache')
  const [reprocessSourceType, setReprocessSourceType] = useState('qrcode')
  const [reprocessConfirmLoading, setReprocessConfirmLoading] = useState(false)
  const [toast, setToast] = useState(null)
  const [confirmMessage, setConfirmMessage] = useState('')
  // filter state
  const [searchSeller, setSearchSeller] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterSource, setFilterSource] = useState('')
  const [receiptImages, setReceiptImages] = useState({})
  const [receiptImageErrors, setReceiptImageErrors] = useState({})
  const receiptImageFetchInFlight = useRef(new Set())
  const receiptById = useMemo(
    () => new Map(receipts.map((receipt) => [receipt.id, receipt])),
    [receipts],
  )

  useEffect(() => {
    fetchReceipts()
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      fetchReceipts()
    }, 30000)

    return () => clearInterval(interval)
  }, [])

  const fetchReceipts = async () => {
    try {
      setLoading(true)
      const response = await apiClient.get('/receipts')
      setReceipts(response.data.data || response.data || [])
      setSelectedReceiptIds([])
    } catch (err) {
      console.error('Failed to load receipts', err)
      setError(t('Unable to load receipts. Please refresh the page.', 'Unable to load receipts. Please refresh the page.'))
    } finally {
      setLoading(false)
    }
  }

  const toggleSelectReceipt = (id) => {
    setSelectedReceiptIds((prev) =>
      prev.includes(id) ? prev.filter((selected) => selected !== id) : [...prev, id]
    )
  }

  const toggleSelectAllReceipts = () => {
    if (selectedReceiptIds.length === receipts.length) {
      setSelectedReceiptIds([])
      return
    }

    setSelectedReceiptIds(receipts.map((receipt) => receipt.id))
  }

  const handleDeleteReceipt = (receiptId) => {
    setPendingReceiptIds([receiptId])
    setConfirmMessage(t('Delete this receipt?', 'Delete this receipt?'))
    setConfirmOpen(true)
  }

  const handleBulkDelete = () => {
    if (!selectedReceiptIds.length) return

    setPendingReceiptIds(selectedReceiptIds)
    const fallback = `Delete ${selectedReceiptIds.length} selected receipt${selectedReceiptIds.length === 1 ? '' : 's'}?`
    setConfirmMessage(t('receipts.bulk_delete_confirm', fallback))
    setConfirmOpen(true)
  }

  const confirmDeleteReceipts = async () => {
    if (!pendingReceiptIds.length) return

    try {
      await Promise.all(
        pendingReceiptIds.map((id) => apiClient.delete(`/receipts/${id}`))
      )
      setSelectedReceiptIds((prev) => prev.filter((id) => !pendingReceiptIds.includes(id)))
      fetchReceipts()
    } catch (err) {
      console.error('Failed to delete receipt(s)', err)
      setError(t('Failed to delete selected receipt(s).', 'Failed to delete selected receipt(s).'))
    } finally {
      setConfirmOpen(false)
      setPendingReceiptIds([])
    }
  }

  const cancelDeleteReceipts = () => {
    setConfirmOpen(false)
    setPendingReceiptIds([])
  }

  const resolvePendingSourceType = (receiptIds) => {
    const sourceTypes = Array.from(new Set(
      receiptIds
        .map((id) => normalizeSourceType(receiptById.get(id)?.source_type))
        .filter(Boolean)
    ))
    if (sourceTypes.length === 1) return sourceTypes[0]
    if (sourceTypes.length > 1) return 'mixed'
    return 'image'
  }

  const buildReprocessPayload = (sourceType, strategy) => {
    const normalizedSourceType = normalizeSourceType(sourceType)
    const normalizedStrategy = String(strategy || '').trim().toLowerCase()
    if (normalizedSourceType === 'speech') {
      // Speech reprocess uses a single default parser; explicit strategy is unnecessary.
      return {}
    }
    if (!normalizedStrategy) return {}
    return { strategy: normalizedStrategy }
  }

  const openReprocessConfirm = (receiptIds, message) => {
    const sourceType = resolvePendingSourceType(receiptIds)
    const defaultStrategy = sourceType === 'mixed'
      ? ''
      : (DEFAULT_REPROCESS_STRATEGY[sourceType] || 'cache')

    setReprocessPendingIds(receiptIds)
    setReprocessSourceType(sourceType)
    setReprocessStrategy(defaultStrategy)
    setReprocessMessage(message)
    setReprocessConfirmOpen(true)
  }

  const promptReprocessReceipt = (receipt) => {
    openReprocessConfirm([receipt.id], t('Reprocess this receipt?', 'Reprocess this receipt?'))
  }

  const confirmReprocessReceipts = async () => {
    if (!reprocessPendingIds.length) return
    try {
      setReprocessConfirmLoading(true)
      setReprocessingIds((prev) => [...prev, ...reprocessPendingIds])
      await Promise.all(reprocessPendingIds.map((id) => {
        const receipt = receiptById.get(id)
        const sourceType = normalizeSourceType(receipt?.source_type)
        const strategyForReceipt = reprocessSourceType === 'mixed'
          ? (DEFAULT_REPROCESS_STRATEGY[sourceType] || '')
          : reprocessStrategy
        return apiClient.post(
          `/receipts/${id}/reprocess`,
          buildReprocessPayload(sourceType, strategyForReceipt),
        )
      }))
      setToast({ type: 'success', message: t('receipts.reprocess_enqueued', `Enqueued ${reprocessPendingIds.length} receipt(s) for reprocessing`) })
      fetchReceipts()
    } catch (err) {
      console.error('Failed to enqueue reprocess', err)
      setToast({ type: 'error', message: t('Failed to enqueue some receipts for reprocessing', 'Failed to enqueue some receipts for reprocessing') })
    } finally {
      setReprocessConfirmOpen(false)
      setReprocessPendingIds([])
      setReprocessConfirmLoading(false)
      setTimeout(() => setToast(null), 5000)
    }
  }

  const cancelReprocessReceipts = () => {
    setReprocessConfirmOpen(false)
    setReprocessPendingIds([])
    setReprocessSourceType('qrcode')
    setReprocessStrategy('cache')
  }

  const handleBulkReprocess = () => {
    if (!selectedReceiptIds.length) return
    openReprocessConfirm(
      selectedReceiptIds,
      t('receipts.bulk_reprocess_confirm', `Reprocess ${selectedReceiptIds.length} selected receipt${selectedReceiptIds.length === 1 ? '' : 's'}?`),
    )
  }

  const handleBulkReview = () => {
    if (!selectedReceiptIds.length) return

    setReviewPendingIds(selectedReceiptIds)
    setReviewAction('review')
    setReviewMessage(t('receipts.bulk_mark_reviewed_confirm', `Mark ${selectedReceiptIds.length} selected receipt${selectedReceiptIds.length === 1 ? '' : 's'} as reviewed?`))
    setReviewConfirmOpen(true)
  }

  const handleBulkUnreview = () => {
    if (!selectedReceiptIds.length) return

    setReviewPendingIds(selectedReceiptIds)
    setReviewAction('unreview')
    setReviewMessage(t('receipts.bulk_mark_unreviewed_confirm', `Mark ${selectedReceiptIds.length} selected receipt${selectedReceiptIds.length === 1 ? '' : 's'} as not reviewed?`))
    setReviewConfirmOpen(true)
  }

  const promptToggleReview = (receiptId, currentlyReviewed) => {
    setReviewPendingIds([receiptId])
    const action = currentlyReviewed ? 'unreview' : 'review'
    setReviewAction(action)
    setReviewMessage(currentlyReviewed ? t('Mark this receipt as not reviewed?', 'Mark this receipt as not reviewed?') : t('Mark this receipt as reviewed?', 'Mark this receipt as reviewed?'))
    setReviewConfirmOpen(true)
  }

  const confirmReviewReceipts = async () => {
    if (!reviewPendingIds.length) return
    try {
      const isSingle = reviewPendingIds.length === 1
      if (reviewAction === 'review') {
        if (isSingle) {
          await apiClient.post(`/receipts/${reviewPendingIds[0]}/review`)
        } else {
          await apiClient.post('/receipts/review', { ids: reviewPendingIds })
        }
        setToast({ type: 'success', message: t('receipts.bulk_marked_reviewed', `Marked ${reviewPendingIds.length} receipt(s) as reviewed`) })
      } else {
        if (isSingle) {
          await apiClient.post(`/receipts/${reviewPendingIds[0]}/unreview`)
        } else {
          await apiClient.post('/receipts/unreview', { ids: reviewPendingIds })
        }
        setToast({ type: 'success', message: t('receipts.bulk_marked_unreviewed', `Marked ${reviewPendingIds.length} receipt(s) as not reviewed`) })
      }

      fetchReceipts()
    } catch (err) {
      console.error('Failed to mark reviewed/unreviewed', err)
      setToast({ type: 'error', message: t('Failed to update some receipts', 'Failed to update some receipts') })
    } finally {
      setReviewConfirmOpen(false)
      setReviewPendingIds([])
      setTimeout(() => setToast(null), 5000)
    }
  }

  const cancelReviewReceipts = () => {
    setReviewConfirmOpen(false)
    setReviewPendingIds([])
  }

  // Client-side filtering: search by seller name, filter by OCR status, filter by source type
  const getReceiptSeller = (receipt) => {
    return receipt.seller_name
      || receipt.ocr_data?.receipt?.seller_name
      || receipt.ocr_data?.receipt?.merchant
      || receipt.parsed_json?.receipt?.seller_name
      || receipt.parsed_json?.receipt?.vendor
      || receipt.parsed_json?.receipt?.merchant
      || ''
  }

  const getReceiptAmount = (receipt) => {
    if (receipt.total_amount != null) return receipt.total_amount
    if (receipt.amount != null) return receipt.amount
    if (receipt.ocr_data?.receipt?.amount != null) return receipt.ocr_data.receipt.amount
    return receipt.parsed_json?.receipt?.total ?? receipt.parsed_json?.receipt?.amount ?? null
  }

  const getReceiptDate = (receipt) => {
    return receipt.receipt_date || receipt.date || receipt.ocr_data?.receipt?.date || receipt.parsed_json?.receipt?.date || receipt.created_at
  }

  const parseSourceData = (sourceData) => {
    if (!sourceData) return null
    if (typeof sourceData === 'object') return sourceData
    if (typeof sourceData === 'string') {
      try {
        const parsed = JSON.parse(sourceData)
        return parsed && typeof parsed === 'object' ? parsed : null
      } catch {
        return null
      }
    }
    return null
  }

  const getInlineImagePreview = (receipt) => {
    const sourceData = parseSourceData(receipt?.source_data ?? receipt?.sourceData)
    if (!sourceData) return null
    return (
      sourceData.base64
      || sourceData.url
      || sourceData.image_url
      || sourceData.imageUrl
      || null
    )
  }

  const arrayBufferToBase64 = (buffer) => {
    let binary = ''
    const bytes = new Uint8Array(buffer)
    for (let i = 0; i < bytes.byteLength; i += 1) {
      binary += String.fromCharCode(bytes[i])
    }
    return window.btoa(binary)
  }

  // Receipts list payloads may not include image data inline, so fetch previews lazily.
  const fetchReceiptImage = async (receiptId) => {
    if (!receiptId || receiptImageFetchInFlight.current.has(receiptId)) return
    receiptImageFetchInFlight.current.add(receiptId)
    try {
      const response = await apiClient.get(`/receipts/${receiptId}/image`, { responseType: 'arraybuffer' })
      const base64Data = arrayBufferToBase64(response.data)
      const contentType = response?.headers?.['content-type'] || 'image/jpeg'
      setReceiptImages((prev) => ({
        ...prev,
        [receiptId]: `data:${contentType};base64,${base64Data}`,
      }))
    } catch (err) {
      setReceiptImageErrors((prev) => ({ ...prev, [receiptId]: true }))
    } finally {
      receiptImageFetchInFlight.current.delete(receiptId)
    }
  }

  useEffect(() => {
    receipts.forEach((receipt) => {
      const sourceType = String(receipt?.source_type || '').toLowerCase()
      if (!['image', 'long_receipt_auto_scan'].includes(sourceType)) return
      if (getInlineImagePreview(receipt)) return
      if (receiptImages[receipt.id]) return
      if (receiptImageErrors[receipt.id]) return
      fetchReceiptImage(receipt.id)
    })
  }, [receipts, receiptImages, receiptImageErrors])

  const renderReceiptPreview = (receipt) => {
    const sourceType = String(receipt?.source_type || '').toLowerCase()

    if (sourceType === 'image' || sourceType === 'long_receipt_auto_scan') {
      const imageSrc = getInlineImagePreview(receipt) || receiptImages[receipt.id]
      if (imageSrc) {
        return (
          <img
            src={imageSrc}
            alt={`Receipt #${receipt.id} preview`}
            className="h-12 w-12 rounded-md border border-outline object-cover"
          />
        )
      }
      if (receiptImageErrors[receipt.id]) {
        return <span className="text-xs text-muted-foreground">No image</span>
      }
      return <span className="text-xs text-muted-foreground">Loading...</span>
    }

    if (sourceType === 'qrcode') {
      return (
        <span
          aria-label="QR receipt source"
          title="QR receipt source"
          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-sky-200 bg-sky-50 text-sky-700"
        >
          <QrCode size={20} />
        </span>
      )
    }

    if (sourceType === 'speech') {
      return (
        <span
          aria-label="Speech receipt source"
          title="Speech receipt source"
          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700"
        >
          <Mic size={20} />
        </span>
      )
    }

    return <span className="text-muted-foreground">{formatEmptyValue(null)}</span>
  }

  const filteredReceipts = useMemo(() => {
    return receipts.filter((r) => {
      const seller = getReceiptSeller(r).toLowerCase()
      const matchesSeller = !searchSeller || seller.includes(searchSeller.toLowerCase())
      const matchesStatus = !filterStatus || r.ocr_status === filterStatus || r.status === filterStatus
      const matchesSource = !filterSource || r.source_type === filterSource
      return matchesSeller && matchesStatus && matchesSource
    })
  }, [receipts, searchSeller, filterStatus, filterSource])

  const getReceiptCategoryLabel = (receipt) => {
    const categories = Array.isArray(receipt?.categories) ? receipt.categories : []
    if (categories.length > 0) {
      return formatCategoryName(categories
        .map((category) => category?.name)
        .filter(Boolean)
        .join(', '))
    }
    if (receipt?.category?.name) return formatCategoryName(receipt.category.name)
    return formatCategoryName(null)
  }

  const reprocessStrategyOptions = reprocessSourceType === 'mixed'
    ? []
    : (REPROCESS_STRATEGY_OPTIONS[reprocessSourceType] || REPROCESS_STRATEGY_OPTIONS.image)

  return (
    <PageShell className="max-w-full">
      {/* Page header */}
      <PageHeader
        title={t('Receipts', 'Receipts')}
        subtitle={`${t('Showing', 'Showing')} ${filteredReceipts.length} ${t('of', 'of')} ${receipts.length} ${t('receipt', 'receipt')}${receipts.length === 1 ? '' : t('s', 's')}.`}
      />

      {toast && (
        <div className={`mt-3 rounded px-4 py-2 ${toast.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-emerald-50 border border-emerald-200 text-emerald-700'}`}>
          {toast.message}
        </div>
      )}

      {error && (
        <div className={ui.alertError}>{error}</div>
      )}

      {/* Filter bar uses Input and native selects matching existing Tailwind patterns */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[160px]">
            <label className={`${ui.label} mb-1`}>{t('Search seller', 'Search seller')}</label>
            <Input
              placeholder={t('Seller name...', 'Seller name...')}
              value={searchSeller}
              onChange={(e) => setSearchSeller(e.target.value)}
            />
          </div>
          <div className="min-w-[140px]">
            <label className={`${ui.label} mb-1`}>{t('OCR status', 'OCR status')}</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className={ui.select}
            >
              <option value="">{t('All statuses', 'All statuses')}</option>
              <option value="pending">{t('Pending', 'Pending')}</option>
              <option value="processing">{t('Processing', 'Processing')}</option>
              <option value="done">{t('Done', 'Done')}</option>
              <option value="completed">{t('Completed', 'Completed')}</option>
              <option value="failed">{t('Failed', 'Failed')}</option>
            </select>
          </div>
          <div className="min-w-[140px]">
            <label className={`${ui.label} mb-1`}>{t('Source', 'Source')}</label>
            <select
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className={ui.select}
            >
              <option value="">{t('All sources', 'All sources')}</option>
              <option value="qrcode">{t('QR Code', 'QR Code')}</option>
          <option value="image">{t('Image', 'Image')}</option>
          <option value="long_receipt_auto_scan">{sourceTypeLabel(t, 'long_receipt_auto_scan')}</option>
              <option value="speech">{t('Speech', 'Speech')}</option>
            </select>
          </div>
          {(searchSeller || filterStatus || filterSource) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSearchSeller(''); setFilterStatus(''); setFilterSource('') }}
            >
              {t('Clear filters', 'Clear filters')}
            </Button>
          )}
        </div>
      </Card>

      {/* Bulk action toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-outline bg-surface-elevated p-4 text-sm text-foreground">
        <div>
          {selectedReceiptIds.length > 0 ? (
            <span>{selectedReceiptIds.length} {t('receipt', 'receipt')}{selectedReceiptIds.length === 1 ? '' : t('s', 's')} {t('selected', 'selected')}</span>
          ) : (
            <span>{t('Select receipts to manage', 'Select receipts to manage')}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleBulkReprocess}
            disabled={!selectedReceiptIds.length}
            className="bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300"
            size="sm"
          >
            {t('Reprocess selected', 'Reprocess selected')}
          </Button>
          <Button
            onClick={handleBulkReview}
            disabled={!selectedReceiptIds.length}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300"
            size="sm"
          >
            {t('Mark reviewed', 'Mark reviewed')}
          </Button>
          <Button
            variant="danger"
            onClick={handleBulkDelete}
            disabled={!selectedReceiptIds.length}
            size="sm"
          >
            {t('Delete selected', 'Delete selected')}
          </Button>
        </div>
      </div>

      {/* Receipts table wrapped in Card */}
      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className={ui.table}>
            <thead className={ui.tableHead}>
              <tr>
                <th className={ui.tableHeadCell}>
                  <input
                    type="checkbox"
                    checked={selectedReceiptIds.length === filteredReceipts.length && filteredReceipts.length > 0}
                    onChange={toggleSelectAllReceipts}
                  />
                </th>
                <th className={ui.tableHeadCell}>{t('Reviewed', 'Reviewed')}</th>
                <th className={ui.tableHeadCell}>{t('Preview', 'Preview')}</th>
                <th className={ui.tableHeadCell}>{t('Added', 'Added')}</th>
                <th className={ui.tableHeadCell}>{t('Receipt date', 'Receipt date')}</th>
                <th className={ui.tableHeadCell}>{t('Seller', 'Seller')}</th>
                <th className={ui.tableHeadCell}>{t('Source', 'Source')}</th>
                <th className={ui.tableHeadCell}>{t('Status', 'Status')}</th>
                <th className={ui.tableHeadCell}>{t('Confidence', 'Confidence')}</th>
                <th className={ui.tableHeadCell}>{t('Category', 'Category')}</th>
                <th className={ui.tableHeadCell}>{t('Products', 'Products')}</th>
                <th className={ui.tableHeadCell}>{t('Amount', 'Amount')}</th>
                <th className={ui.tableHeadCell}>{t('Actions', 'Actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-divider bg-surface text-sm text-foreground">
              {loading ? (
                <tr>
                  <td colSpan={13} className="px-6 py-8 text-center text-muted-foreground">{t('Loading receipts...', 'Loading receipts...')}</td>
                </tr>
              ) : filteredReceipts.length === 0 ? (
                <tr>
                  <td colSpan={13} className="px-6 py-8 text-center text-muted-foreground">{t('No receipts found.', 'No receipts found.')}</td>
                </tr>
              ) : (
                filteredReceipts.map((receipt) => {
                  // derive strategy from pipeline snapshot in ocr_raw_json
                  const strategy = receipt.ocr_raw_json?.qrPipeline?.strategy ?? null
                  const confidence = receipt.confidence ?? receipt.ocr_raw_json?.qrPipeline?.confidence ?? null
                  return (
                    <tr key={receipt.id} className={selectedReceiptIds.includes(receipt.id) ? 'bg-surface-elevated' : ''}>
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedReceiptIds.includes(receipt.id)}
                          onChange={() => toggleSelectReceipt(receipt.id)}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={Boolean(receipt.is_reviewed)}
                          disabled
                          aria-label={receipt.is_reviewed ? t('Reviewed', 'Reviewed') : t('Not reviewed', 'Not reviewed')}
                          className="h-4 w-4 rounded border-outline text-blue-600"
                        />
                      </td>
                      <td className="px-6 py-4">{renderReceiptPreview(receipt)}</td>
                      <td className="px-6 py-4 text-muted-foreground">{receipt.created_at ? formatDateTime(receipt.created_at) : formatEmptyValue(null)}</td>
                      <td className="px-6 py-4 text-muted-foreground">{getReceiptDate(receipt) ? formatDateTime(getReceiptDate(receipt)) : formatEmptyValue(null)}</td>
                      <td className="px-6 py-4 text-foreground">
                        {formatEmptyValue(getReceiptSeller(receipt))}
                      </td>
                      {/* source_type column qrcode / image / speech */}
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700">
                          {sourceTypeLabel(t, receipt.source_type)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {(!receipt.source_type || receipt.source_type === 'image' || receipt.source_type === 'long_receipt_auto_scan') ? (
                            <>
                              <StatusBadge label={t('OCR', 'OCR')} status={receipt.ocr_status} />
                              <StatusBadge label={t('Products', 'Products')} status={receipt.product_analysis_status} />
                              <StatusBadge label={t('Categorization', 'Categorization')} status={receipt.product_translation_status} />
                            </>
                          ) : receipt.source_type === 'qrcode' ? (
                            <>
                              <StatusBadge label={t('QRcode', 'QRcode')} status={receipt.qr_code_product_extraction_status} />
                              <StatusBadge label={t('Categorization', 'Categorization')} status={receipt.product_translation_status} />
                            </>
                          ) : receipt.source_type === 'speech' ? (
                            <>
                              <StatusBadge label={t('Speech', 'Speech')} status={receipt.ocr_status} />
                              <StatusBadge label={t('Categorization', 'Categorization')} status={receipt.product_translation_status} />
                            </>
                          ) : (
                            <span className="inline-flex rounded-full bg-surface-elevated px-2 py-1 text-xs font-semibold text-foreground">
                              {receipt.status || t('unknown', 'unknown')}
                            </span>
                          )}
                        </div>
                      </td>
                      {/* confidence score shown as percentage */}
                      <td className="px-6 py-4 text-foreground">
                        {formatConfidence(confidence)}
                      </td>
                      <td className="px-6 py-4 text-foreground">{getReceiptCategoryLabel(receipt)}</td>
                      <td className="px-6 py-4 text-foreground">{receipt.products_count ?? receipt.products?.length ?? 0}</td>
                      <td className="px-6 py-4 text-foreground">
                        {getReceiptAmount(receipt) != null
                          ? formatCurrency(getReceiptAmount(receipt), receipt.currency || receipt.parsed_json?.receipt?.currency || receipt.parsed_json?.receipt?.summary?.currency || receipt.parsed_json?.summary?.currency || receipt.ocr_data?.receipt?.currency || 'EUR')
                          : formatEmptyValue(null)}
                      </td>
                      <td className="px-6 py-4 text-foreground">
                        <div className="flex flex-col gap-2">
                          <Button size="sm" onClick={() => navigate(`/receipts/${receipt.id}`)}>
                            {t('View', 'View')}
                          </Button>
                          <Button size="sm" variant="primary" onClick={() => navigate(`/receipts/${receipt.id}?edit=1`)}>
                            {t('Edit', 'Edit')}
                          </Button>
                          {/* retry / reprocess button */}
                          <Button
                            size="sm"
                            className="bg-amber-600 hover:bg-amber-700"
                            onClick={() => promptReprocessReceipt(receipt)}
                            disabled={reprocessingIds.includes(receipt.id)}
                          >
                            {reprocessingIds.includes(receipt.id) ? t('Reprocessing...', 'Reprocessing...') : t('Retry', 'Retry')}
                          </Button>
                          <Button
                            size="sm"
                            className={receipt.is_reviewed ? 'bg-emerald-300' : 'bg-emerald-600 hover:bg-emerald-700'}
                            onClick={() => promptToggleReview(receipt.id, receipt.is_reviewed)}
                          >
                            {receipt.is_reviewed ? t('Unreview', 'Unreview') : t('Review', 'Review')}
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteReceipt(receipt.id)}
                          >
                            {t('Delete', 'Delete')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        title={t('Confirm delete', 'Confirm delete')}
        message={confirmMessage}
        confirmText={t('Delete', 'Delete')}
        cancelText={t('Cancel', 'Cancel')}
        onConfirm={confirmDeleteReceipts}
        onCancel={cancelDeleteReceipts}
        loading={loading}
      />
      <ConfirmDialog
        open={reprocessConfirmOpen}
        title={t('Confirm reprocess', 'Confirm reprocess')}
        message={reprocessMessage}
        confirmText={t('Reprocess', 'Reprocess')}
        cancelText={t('Cancel', 'Cancel')}
        onConfirm={confirmReprocessReceipts}
        onCancel={cancelReprocessReceipts}
        loading={reprocessConfirmLoading}
      >
        {reprocessSourceType === 'mixed' ? (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {t('Mixed receipt types selected. Each receipt will use its own default strategy.', 'Mixed receipt types selected. Each receipt will use its own default strategy.')}
          </div>
        ) : (
          <div className="mt-4">
            <label className="block text-sm font-medium text-foreground">{t('Reprocessing strategy', 'Reprocessing strategy')}</label>
            <select
              value={reprocessStrategy}
              onChange={(e) => setReprocessStrategy(e.target.value)}
              className={`${ui.select} mt-2 w-full`}
            >
              {reprocessStrategyOptions.map((option) => (
                <option key={option.value} value={option.value}>{resolveReprocessStrategyLabel(t, option.labelKey)}</option>
              ))}
            </select>
          </div>
        )}
      </ConfirmDialog>
      <ConfirmDialog
        open={reviewConfirmOpen}
        title={t('Confirm review', 'Confirm review')}
        message={reviewMessage}
        confirmText={t('Mark reviewed', 'Mark reviewed')}
        cancelText={t('Cancel', 'Cancel')}
        onConfirm={confirmReviewReceipts}
        onCancel={cancelReviewReceipts}
        loading={false}
      />
    </PageShell>
  )
}

